/**
 * Picker — pointer picking for selection + hover. POINTERTAP: tapping a panel
 * selects it; tapping the background (skybox / empty) deselects; double-tap
 * focuses the camera. POINTERMOVE drives the hover highlight (handleHoverIn/Out).
 */

import { PointerEventTypes, type Scene, type Observer, type PointerInfo } from 'babylonjs';
import type { EditorElementLike } from '../content/EditorContentElement';

export interface PickerCallbacks {
  onSelect(sceneID: string): void;
  onDeselect(): void;
  onFocus(sceneID: string): void;
  /** When true, hover picking is suppressed (e.g. during a gizmo transform). */
  isHoverSuppressed?(): boolean;
}

export class Picker {
  private scene: Scene;
  private callbacks: PickerCallbacks;
  private observer: Observer<PointerInfo> | null = null;
  private lastTapTime = 0;
  private lastTapId: string | null = null;
  private hovered: EditorElementLike | null = null;

  constructor(scene: Scene, callbacks: PickerCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.observer = this.scene.onPointerObservable.add((info) => {
      if (info.type === PointerEventTypes.POINTERTAP) {
        this._handleTap(info);
      } else if (info.type === PointerEventTypes.POINTERMOVE) {
        this._handleMove();
      }
    });
  }

  /** Hover highlight: pick under the pointer and diff against the last hovered panel. */
  private _handleMove(): void {
    // While transforming (gizmo drag), the cursor sweeps over other elements —
    // don't let them highlight. Clear any existing hover and bail.
    if (this.callbacks.isHoverSuppressed?.()) {
      if (this.hovered) { this.hovered.handleHoverOut(); this.hovered = null; }
      return;
    }
    const pick = this.scene.pick(this.scene.pointerX, this.scene.pointerY);
    const element = pick?.pickedMesh?.metadata?.editorElement as EditorElementLike | undefined;
    const next = element ?? null;
    if (next === this.hovered) return;
    this.hovered?.handleHoverOut();
    next?.handleHoverIn();
    this.hovered = next;
  }

  private _handleTap(info: PointerInfo): void {
    const mesh = info.pickInfo?.pickedMesh;
    const element = mesh?.metadata?.editorElement as EditorElementLike | undefined;

    if (element) {
      const now = performance.now();
      const isDoubleTap = this.lastTapId === element.sceneID && now - this.lastTapTime < 300;
      this.callbacks.onSelect(element.sceneID);
      if (isDoubleTap) this.callbacks.onFocus(element.sceneID);
      this.lastTapTime = now;
      this.lastTapId = element.sceneID;
    } else {
      // Background (skybox) or empty → deselect.
      this.callbacks.onDeselect();
      this.lastTapId = null;
    }
  }

  dispose(): void {
    if (this.observer) {
      this.scene.onPointerObservable.remove(this.observer);
      this.observer = null;
    }
  }
}

export default Picker;
